/**
 * Governance operations for aaac init / update / uninstall.
 *
 * Self-contained implementation (mirrors packages/runtime/src/lib/governance-hooks.ts
 * and the relevant parts of packages/runtime/src/generator/config.ts) to avoid a
 * circular package dependency: @aaac/runtime already depends on @aaac/contracts.
 *
 * Keep in sync with the runtime counterpart when modifying managed-block semantics.
 *
 * .cursor/hooks.json is written in Cursor's real schema:
 *   { "version": 1, "hooks": { "<event>": [{ "command": "..." }] } }
 * A legacy flat-array format ([{ event, command }]) is still parsed for
 * backward compatibility and migrated to the nested schema on write.
 */

import { createHash } from "node:crypto";
import { existsSync } from "node:fs";
import { chmod, mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname, join, resolve } from "node:path";
import { parse as parseYaml } from "yaml";
import { z } from "zod";
import { generateObservConfig } from "./observ-config.js";
import {
  isManagedRecorderPath,
  materializeRecorderScript,
  unmaterializeRecorderScript,
} from "./recorder-script.js";

// ---------------------------------------------------------------------------
// Constants (mirror governance-hooks.ts)
// ---------------------------------------------------------------------------

export const MANAGED_BLOCK_START = "# >>> aaac-governance:managed (do not edit) >>>";
export const MANAGED_BLOCK_END = "# <<< aaac-governance:managed <<<";
export const GOVERNANCE_LOCK_PATH = ".agent-logs/governance-manifest.lock";

const DEFAULT_RECORDER = ".cursor/hooks/observ-record.sh";
const DEFAULT_CURSOR_HOOKS_JSON = ".cursor/hooks.json";
const DEFAULT_BINARY_DEST = "bin/aaac.js";
const CURSOR_HOOKS_VERSION = 1;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Flat internal representation of a single Cursor hook binding.
 * Used for building defaults and merging; serialized into the nested
 * {@link CursorHooksFile} schema before writing to disk.
 */
export interface CursorHookEntry {
  event: string;
  command: string;
  [key: string]: unknown;
}

/** A single command binding under an event in Cursor's hooks.json schema. */
export interface CursorHookCommand {
  command: string;
  [key: string]: unknown;
}

/** Cursor's real .cursor/hooks.json schema. */
export interface CursorHooksFile {
  version: number;
  hooks: Record<string, CursorHookCommand[]>;
}

export type GovernanceLockEntryType =
  | "git-hook"
  | "cursor-hook"
  | "generated"
  | "recorder-script";

export interface MaterializeResult {
  materialized: Array<{ path: string; action: "created" | "updated" | "unchanged" }>;
  lockEntries: Array<{ path: string; type: GovernanceLockEntryType }>;
}

export type GitHookIssueKind = "missing" | "empty" | "missing_managed_block";

export interface GitHookIssue {
  hookName: string;
  kind: GitHookIssueKind;
}

export interface GovernanceLockFile {
  materialized_at: string;
  entries: Array<{ path: string; type: GovernanceLockEntryType }>;
}

export interface GovernanceMaterializeOptions {
  projectRoot: string;
  environments: {
    git?: { hooks?: string[] };
    cursor?: { hooks_json?: string };
  };
  observabilityBinding?: {
    event_mapping?: string;
    recorder?: string;
  };
  /**
   * Path to artifact-contracts.yaml (relative to projectRoot), used to generate
   * .agent-logs/config/artifact-lookup.json. Sourced from project.yaml `artifacts`.
   */
  artifactContracts?: string;
  /**
   * Gate commands declared in bindings.hooks, keyed by git hook name
   * (e.g. "pre-commit" -> ["npm run lint", "npx tsc --noEmit"]).
   * Each command is run sequentially in the hook's managed block and a
   * non-zero exit fails the hook (acts as a gate).
   */
  hookCommands?: Record<string, string[]>;
}

// ---------------------------------------------------------------------------
// Minimal project.yaml schema (environments + bindings only)
// ---------------------------------------------------------------------------

const EnvironmentsSchema = z
  .object({
    git: z.object({ hooks: z.array(z.string()).optional() }).optional(),
    cursor: z.object({ hooks_json: z.string().optional() }).optional(),
  })
  .optional();

const BindingsObservabilitySchema = z
  .object({
    event_mapping: z.string().optional(),
    recorder: z.string().optional(),
  })
  .optional();

const BindingsSchema = z
  .object({
    observability: BindingsObservabilitySchema,
    hooks: z.array(z.string()).optional(),
  })
  .optional();

const InitProjectConfigSchema = z.object({
  environments: EnvironmentsSchema,
  bindings: BindingsSchema,
  artifacts: z.string().optional(),
});

export type InitProjectConfig = z.infer<typeof InitProjectConfigSchema>;

export async function loadInitConfig(configPath: string): Promise<InitProjectConfig> {
  const absPath = resolve(configPath);
  const raw = await readFile(absPath, "utf8");
  const parsed = parseYaml(raw) as unknown;
  return InitProjectConfigSchema.parse(parsed ?? {});
}

// ---------------------------------------------------------------------------
// Hook bindings (bindings.hooks) — git hook gate commands
// ---------------------------------------------------------------------------

const HookCommandSchema = z.object({
  command: z.string(),
  description: z.string().optional(),
});

const HookBindingSchema = z.object({
  hooks: z.record(z.string(), z.array(HookCommandSchema)).optional(),
});

/**
 * Load one or more bindings.hooks YAML files and aggregate their declared
 * gate commands into a single map keyed by git hook name.
 *
 * Binding paths are resolved relative to {@link projectRoot}. Missing files
 * are skipped (the binding is declarative — absence is not fatal). Commands
 * from multiple bindings for the same hook are concatenated in declaration
 * order; exact-duplicate commands are de-duplicated to keep repeated runs
 * idempotent.
 */
export async function loadHookBindings(
  projectRoot: string,
  bindingPaths: string[] | undefined,
): Promise<Record<string, string[]>> {
  const result: Record<string, string[]> = {};
  if (!bindingPaths?.length) return result;

  const root = resolve(projectRoot);
  for (const bindingPath of bindingPaths) {
    const absPath = resolve(root, bindingPath);
    if (!existsSync(absPath)) continue;

    const raw = await readFile(absPath, "utf8");
    const parsed = HookBindingSchema.parse(parseYaml(raw) ?? {});
    if (!parsed.hooks) continue;

    for (const [hookName, commands] of Object.entries(parsed.hooks)) {
      const list = result[hookName] ?? [];
      for (const { command } of commands) {
        if (!list.includes(command)) list.push(command);
      }
      result[hookName] = list;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Managed-block helpers (mirror governance-hooks.ts)
// Use a fresh RegExp per call to avoid global-flag lastIndex state issues.
// ---------------------------------------------------------------------------

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function createManagedBlockRegex(): RegExp {
  return new RegExp(
    `${escapeRegex(MANAGED_BLOCK_START)}[\\s\\S]*?${escapeRegex(MANAGED_BLOCK_END)}\\n?`,
    "g",
  );
}

function buildManagedBlock(blockContent: string): string {
  return `${MANAGED_BLOCK_START}\n${blockContent.trimEnd()}\n${MANAGED_BLOCK_END}\n`;
}

export function injectManagedBlock(existingContent: string, blockContent: string): string {
  const managedBlock = buildManagedBlock(blockContent);
  const re = createManagedBlockRegex();

  if (re.test(existingContent)) {
    return existingContent.replace(createManagedBlockRegex(), managedBlock);
  }

  const trimmed = existingContent.replace(/\s+$/, "");
  if (trimmed.length === 0) {
    return `#!/bin/sh\n${managedBlock}`;
  }

  const separator = trimmed.endsWith("\n") ? "" : "\n";
  return `${trimmed}${separator}${managedBlock}`;
}

export function removeManagedBlock(existingContent: string): string {
  if (!existingContent.includes(MANAGED_BLOCK_START)) {
    return existingContent;
  }

  const withoutBlock = existingContent.replace(createManagedBlockRegex(), "");
  const meaningful = withoutBlock.split("\n").filter((line) => {
    const t = line.trim();
    return t.length > 0 && t !== "#!/bin/sh" && t !== "#!/bin/bash";
  });

  if (meaningful.length === 0) {
    return "";
  }

  return withoutBlock.replace(/\n{3,}/g, "\n\n").replace(/\s+$/, "\n");
}

// ---------------------------------------------------------------------------
// Cursor-hook merge helpers (mirror governance-hooks.ts)
// ---------------------------------------------------------------------------

export function parseCursorHooksFile(raw: string | undefined): CursorHooksFile {
  if (!raw || !raw.trim()) {
    return { version: CURSOR_HOOKS_VERSION, hooks: {} };
  }

  const parsed = JSON.parse(raw) as unknown;

  // Legacy flat-array format: [{ event, command }, ...]
  if (Array.isArray(parsed)) {
    return flatEntriesToFile(
      parsed.filter(
        (entry): entry is CursorHookEntry =>
          entry !== null &&
          typeof entry === "object" &&
          typeof (entry as CursorHookEntry).event === "string" &&
          typeof (entry as CursorHookEntry).command === "string",
      ),
    );
  }

  if (parsed && typeof parsed === "object") {
    const obj = parsed as Record<string, unknown>;
    const version = typeof obj.version === "number" ? obj.version : CURSOR_HOOKS_VERSION;
    const hooks: Record<string, CursorHookCommand[]> = {};
    const rawHooks = obj.hooks;
    if (rawHooks && typeof rawHooks === "object" && !Array.isArray(rawHooks)) {
      for (const [event, commands] of Object.entries(rawHooks as Record<string, unknown>)) {
        if (Array.isArray(commands)) {
          hooks[event] = commands.filter(
            (c): c is CursorHookCommand =>
              c !== null &&
              typeof c === "object" &&
              typeof (c as CursorHookCommand).command === "string",
          );
        }
      }
    }
    return { version, hooks };
  }

  return { version: CURSOR_HOOKS_VERSION, hooks: {} };
}

export function serializeCursorHooksFile(file: CursorHooksFile): string {
  return JSON.stringify(file, null, 2) + "\n";
}

function flatEntriesToFile(entries: CursorHookEntry[]): CursorHooksFile {
  return mergeCursorHooks({ version: CURSOR_HOOKS_VERSION, hooks: {} }, entries);
}

/**
 * Idempotently merge new hook entries into an existing Cursor hooks file.
 *
 * Entries are keyed by `(event, command)`: an existing command is updated in
 * place, a new one is appended. This lets multiple independent writers (the
 * observability recorder, the cursor binding, future guardrail hooks) share a
 * single `.cursor/hooks.json` without clobbering each other — no single writer
 * owns the file. Merge order does not affect the final set.
 */
export function mergeCursorHooks(
  existing: CursorHooksFile,
  newEntries: CursorHookEntry[],
): CursorHooksFile {
  const hooks: Record<string, CursorHookCommand[]> = {};
  for (const [event, commands] of Object.entries(existing.hooks)) {
    hooks[event] = commands.map((c) => ({ ...c }));
  }

  for (const entry of newEntries) {
    const { event, ...command } = entry;
    const list = hooks[event] ?? [];
    const idx = list.findIndex((c) => c.command === command.command);
    if (idx >= 0) {
      list[idx] = command;
    } else {
      list.push(command);
    }
    hooks[event] = list;
  }

  return { version: existing.version ?? CURSOR_HOOKS_VERSION, hooks };
}

function removeCursorHooks(
  existing: CursorHooksFile,
  commands: string[],
): CursorHooksFile {
  const removeSet = new Set(commands);
  const hooks: Record<string, CursorHookCommand[]> = {};
  for (const [event, list] of Object.entries(existing.hooks)) {
    const filtered = list.filter((c) => !removeSet.has(c.command));
    if (filtered.length > 0) {
      hooks[event] = filtered;
    }
  }
  return { version: existing.version ?? CURSOR_HOOKS_VERSION, hooks };
}

/**
 * Build the managed-block body for a git hook.
 *
 * The observability recorder runs first and is fail-open (`|| true`): a
 * recorder failure never blocks the commit/push. Each declared gate command
 * then runs sequentially; a non-zero exit fails the hook (`|| exit 1`).
 *
 * `exec` is intentionally NOT used — it would replace the shell process and
 * prevent the gate commands from running.
 *
 * Keep in sync with buildGitHookBlock in
 * packages/runtime/src/lib/governance-hooks.ts.
 */
function buildGitHookBlock(
  hookName: string,
  recorder: string,
  gateCommands: string[] = [],
): string {
  const lines = [`${recorder} ${hookName} "$@" || true`];
  for (const command of gateCommands) {
    lines.push(`${command} || exit 1`);
  }
  return lines.join("\n");
}

/**
 * Cursor hook events recorded by the observability recorder.
 *
 * Mirrors the full set required by
 * docs/observability-spec/shift-left-event-mapping.md §1.1/§2.
 * `beforeShellExecution` is intentionally excluded — it is wired separately to
 * the guardrail (guard evaluate) hook and merged in via mergeCursorHooks.
 *
 * Keep in sync with DEFAULT_CURSOR_HOOK_EVENTS in
 * packages/runtime/src/lib/governance-hooks.ts.
 */
const DEFAULT_CURSOR_HOOK_EVENTS = [
  "sessionStart",
  "sessionEnd",
  "subagentStart",
  "subagentStop",
  "beforeSubmitPrompt",
  "afterShellExecution",
  "preToolUse",
  "postToolUse",
  "postToolUseFailure",
  "afterFileEdit",
  "preCompact",
  "stop",
] as const;

function buildDefaultCursorHooks(recorder: string): CursorHookEntry[] {
  return DEFAULT_CURSOR_HOOK_EVENTS.map((event) => ({
    event,
    command: `${recorder} ${event}`,
  }));
}

// ---------------------------------------------------------------------------
// File helpers
// ---------------------------------------------------------------------------

async function ensureParentDir(filePath: string): Promise<void> {
  await mkdir(dirname(filePath), { recursive: true });
}

async function readTextFileIfExists(filePath: string): Promise<string | undefined> {
  if (!existsSync(filePath)) return undefined;
  return readFile(filePath, "utf8");
}

// ---------------------------------------------------------------------------
// Lock file (mirror governance-hooks.ts)
// ---------------------------------------------------------------------------

export async function writeGovernanceLock(
  projectRoot: string,
  entries: MaterializeResult["lockEntries"],
): Promise<string> {
  const lockPath = join(projectRoot, GOVERNANCE_LOCK_PATH);
  const lock: GovernanceLockFile = {
    materialized_at: new Date().toISOString(),
    entries,
  };
  await ensureParentDir(lockPath);
  await writeFile(lockPath, JSON.stringify(lock, null, 2) + "\n", "utf8");
  return GOVERNANCE_LOCK_PATH;
}

export async function readGovernanceLock(
  projectRoot: string,
): Promise<GovernanceLockFile | undefined> {
  const lockPath = join(projectRoot, GOVERNANCE_LOCK_PATH);
  if (!existsSync(lockPath)) return undefined;
  const raw = await readFile(lockPath, "utf8");
  if (!raw.trim()) return undefined;
  return JSON.parse(raw) as GovernanceLockFile;
}

/**
 * Inspect configured git hooks for missing, empty, or unmanaged content.
 * Used by `aaac update` to warn before repair (#208).
 */
export async function inspectGitHooks(
  projectRoot: string,
  hookNames: string[],
): Promise<GitHookIssue[]> {
  const root = resolve(projectRoot);
  const issues: GitHookIssue[] = [];

  for (const hookName of hookNames) {
    const absPath = join(root, ".git/hooks", hookName);
    if (!existsSync(absPath)) {
      issues.push({ hookName, kind: "missing" });
      continue;
    }

    const existing = await readTextFileIfExists(absPath);
    if (!existing || existing.trim().length === 0) {
      issues.push({ hookName, kind: "empty" });
    } else if (!existing.includes(MANAGED_BLOCK_START)) {
      issues.push({ hookName, kind: "missing_managed_block" });
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// materializeHooks (mirror governance-hooks.ts)
// ---------------------------------------------------------------------------

export async function materializeHooks(
  options: GovernanceMaterializeOptions,
): Promise<MaterializeResult> {
  const projectRoot = resolve(options.projectRoot);
  const result: MaterializeResult = { materialized: [], lockEntries: [] };
  const recorder = options.observabilityBinding?.recorder ?? DEFAULT_RECORDER;

  // --- git hooks ---
  if (options.environments.git?.hooks?.length) {
    for (const hookName of options.environments.git.hooks) {
      const relPath = join(".git/hooks", hookName);
      const absPath = join(projectRoot, relPath);
      const blockContent = buildGitHookBlock(
        hookName,
        recorder,
        options.hookCommands?.[hookName],
      );

      const existing = (await readTextFileIfExists(absPath)) ?? "";
      const updated = injectManagedBlock(existing, blockContent);

      let action: "created" | "updated" | "unchanged" = "unchanged";
      if (!existsSync(absPath)) {
        action = "created";
      } else if (updated !== existing) {
        action = "updated";
      }

      if (action !== "unchanged") {
        await ensureParentDir(absPath);
        await writeFile(absPath, updated, "utf8");
        await chmod(absPath, 0o755);
      }

      result.materialized.push({ path: relPath, action });
      result.lockEntries.push({ path: relPath, type: "git-hook" });
    }
  }

  // --- cursor hooks ---
  if (options.environments.cursor) {
    const rawHooksJsonValue = options.environments.cursor.hooks_json;
    // resolve() handles both relative (against projectRoot) and absolute paths correctly
    const hooksJsonPath = rawHooksJsonValue
      ? resolve(projectRoot, rawHooksJsonValue)
      : join(projectRoot, DEFAULT_CURSOR_HOOKS_JSON);

    const relPath = hooksJsonPath.startsWith(projectRoot + "/")
      ? hooksJsonPath.slice(projectRoot.length + 1)
      : DEFAULT_CURSOR_HOOKS_JSON;

    const newEntries = buildDefaultCursorHooks(recorder);
    const existingRaw = await readTextFileIfExists(hooksJsonPath);
    const existing = parseCursorHooksFile(existingRaw);
    const merged = mergeCursorHooks(existing, newEntries);
    const serialized = serializeCursorHooksFile(merged);

    let action: "created" | "updated" | "unchanged" = "unchanged";
    if (!existsSync(hooksJsonPath)) {
      action = "created";
    } else if (serialized !== existingRaw) {
      action = "updated";
    }

    if (action !== "unchanged") {
      await ensureParentDir(hooksJsonPath);
      await writeFile(hooksJsonPath, serialized, "utf8");
    }

    result.materialized.push({ path: relPath, action });
    result.lockEntries.push({ path: relPath, type: "cursor-hook" });
  }

  // --- observability config (.agent-logs/config/*.json) ---
  // Generated only when an observability binding is configured. Deterministic
  // and idempotent (see observ-config.ts).
  if (options.observabilityBinding) {
    if (isManagedRecorderPath(recorder)) {
      const scriptResult = await materializeRecorderScript(projectRoot, recorder);
      result.materialized.push(scriptResult);
      result.lockEntries.push({ path: scriptResult.path, type: "recorder-script" });
    }

    const { generated, lockEntries } = await generateObservConfig({
      projectRoot,
      eventMapping: options.observabilityBinding.event_mapping,
      artifactContracts: options.artifactContracts,
    });
    for (const g of generated) {
      result.materialized.push({ path: g.path, action: g.action });
    }
    result.lockEntries.push(...lockEntries);
  }

  // --- write lock file ---
  if (result.lockEntries.length > 0) {
    const lockExisted = existsSync(join(projectRoot, GOVERNANCE_LOCK_PATH));
    await writeGovernanceLock(projectRoot, result.lockEntries);
    result.materialized.push({
      path: GOVERNANCE_LOCK_PATH,
      action: lockExisted ? "updated" : "created",
    });
    result.lockEntries.push({ path: GOVERNANCE_LOCK_PATH, type: "generated" });
  }

  return result;
}

// ---------------------------------------------------------------------------
// unmaterializeHooks (mirror governance-hooks.ts)
// ---------------------------------------------------------------------------

export async function unmaterializeHooks(
  projectRoot: string,
  lockEntries: MaterializeResult["lockEntries"],
): Promise<void> {
  const root = resolve(projectRoot);

  for (const entry of lockEntries) {
    if (entry.type === "generated") {
      const absPath = join(root, entry.path);
      if (existsSync(absPath)) {
        await writeFile(absPath, "", "utf8");
      }
      continue;
    }

    if (entry.type === "git-hook") {
      const absPath = join(root, entry.path);
      const existing = await readTextFileIfExists(absPath);
      if (existing === undefined) continue;

      const updated = removeManagedBlock(existing);
      await writeFile(absPath, updated, "utf8");
      continue;
    }

    if (entry.type === "cursor-hook") {
      const absPath = join(root, entry.path);
      const existingRaw = await readTextFileIfExists(absPath);
      if (existingRaw === undefined) continue;

      const existing = parseCursorHooksFile(existingRaw);
      const commandsToRemove = buildDefaultCursorHooks(DEFAULT_RECORDER).map((h) => h.command);
      const updated = removeCursorHooks(existing, commandsToRemove);
      await writeFile(absPath, serializeCursorHooksFile(updated), "utf8");
      continue;
    }

    if (entry.type === "recorder-script") {
      await unmaterializeRecorderScript(root, entry.path);
    }
  }

  // Clear the lock file itself
  const lockPath = join(root, GOVERNANCE_LOCK_PATH);
  if (existsSync(lockPath)) {
    await writeFile(lockPath, "", "utf8");
  }
}

// ---------------------------------------------------------------------------
// Binary copy (SHA256 idempotent)
// ---------------------------------------------------------------------------

export async function copyCLIBinary(
  srcPath: string,
  projectRoot: string,
  destRelPath = DEFAULT_BINARY_DEST,
): Promise<{ path: string; action: "created" | "updated" | "unchanged" }> {
  const dstPath = join(projectRoot, destRelPath);
  const srcContent = await readFile(srcPath);
  const srcHash = createHash("sha256").update(srcContent).digest("hex");

  if (existsSync(dstPath)) {
    const dstContent = await readFile(dstPath);
    const dstHash = createHash("sha256").update(dstContent).digest("hex");
    if (srcHash === dstHash) {
      return { path: destRelPath, action: "unchanged" };
    }
    await writeFile(dstPath, srcContent);
    await chmod(dstPath, 0o755);
    return { path: destRelPath, action: "updated" };
  }

  await ensureParentDir(dstPath);
  await writeFile(dstPath, srcContent);
  await chmod(dstPath, 0o755);
  return { path: destRelPath, action: "created" };
}

// ---------------------------------------------------------------------------
// Scaffold project.yaml template
// ---------------------------------------------------------------------------

export const SCAFFOLD_PROJECT_YAML = `schema: aaac/project/0.1

# Uncomment and configure to enable git hook governance:
#
# environments:
#   git:
#     # commit-msg/post-commit are observability-only (promotion axis): post-commit
#     # records promotion.commit + promotion.file, feeding Enricher R1/R2/R5.
#     hooks: [pre-commit, commit-msg, post-commit, pre-push]
#   cursor:
#     hooks_json: .cursor/hooks.json
#
# bindings:
#   observability:
#     event_mapping: ./bindings/observability.yaml
#     recorder: aaac-observ
#   hooks:
#     # Each file declares gate commands per git hook; a non-zero exit
#     # blocks the commit/push. Only hooks listed in environments.git.hooks
#     # are materialized.
#     - ./bindings/git.yaml
`;
